import { useEffect, useRef } from 'react';
import type { FieldErrors, FieldValues, FormState } from 'react-hook-form';
import { useAssistantSafe } from './AssistantProvider';

/** Every failing field as a dotted path — names only, never values. */
export function allErrorPaths(errors: FieldErrors, prefix = '', depth = 0): string[] {
  return Object.entries(errors).flatMap(([key, value]) => {
    if (!value || key === 'root') {
      return [];
    }
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof (value as { message?: unknown }).message === 'string' || depth >= 4) {
      return [path];
    }
    return allErrorPaths(value as FieldErrors, path, depth + 1);
  });
}

/** First failing field as a dotted path — names only, never values. */
export function firstErrorPath(errors: FieldErrors, prefix = '', depth = 0): string | undefined {
  for (const [key, value] of Object.entries(errors)) {
    if (!value || key === 'root') {
      continue;
    }
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof (value as { message?: unknown }).message === 'string' || depth >= 4) {
      return path;
    }
    const nested = firstErrorPath(value as FieldErrors, path, depth + 1);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

/**
 * Records a `validation_failed` journal event each time a step submit is
 * rejected by client-side validation. Renders nothing; a no-op when the
 * assistant is absent or off.
 */
export function ValidationFailureJournal<TForm extends FieldValues>({
  formState,
  stepKey,
}: {
  formState: FormState<TForm>;
  stepKey: string;
}) {
  const assistant = useAssistantSafe();
  const recordEvent = assistant?.recordEvent;
  const resolveValidationFailures = assistant?.resolveValidationFailures;
  const lastRecordedSubmitCount = useRef(formState.submitCount);
  // Deliberately not reading formState.isValid: subscribing to it makes
  // react-hook-form run the whole schema validation eagerly, which is both
  // wasted work and (on some step schemas) triggers async rejections outside
  // any await. A rejected submit is already visible as submitCount advancing
  // while errors is non-empty.
  const { submitCount, errors } = formState;
  const failedField = firstErrorPath(errors);
  // Joined so the effect compares by value, not by a new array each render.
  const failingPaths = allErrorPaths(errors).join('\u0000');

  useEffect(() => {
    const isNewSubmit = submitCount > lastRecordedSubmitCount.current;
    lastRecordedSubmitCount.current = submitCount;
    if (!isNewSubmit) return;
    // Every submit retracts the failures the customer has since fixed, whether
    // or not the form is now wholly valid — a step can fail on one field while
    // another was corrected, and a stale entry makes the assistant report a
    // problem that no longer exists.
    resolveValidationFailures?.(stepKey, failingPaths ? failingPaths.split('\u0000') : []);
    if (failedField) {
      recordEvent?.({ type: 'validation_failed', stepKey, fieldName: failedField });
    }
  }, [submitCount, failedField, failingPaths, stepKey, recordEvent, resolveValidationFailures]);

  return null;
}
